import type { TelegramWebApp } from "./types";

/** `null` outside Telegram (plain browser testing) — every caller must handle that. */
export function getTelegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export const tg = getTelegram();

/**
 * Every SDK call in this app goes through here or `versionGate` below.
 * Some methods (`enableClosingConfirmation`, `disableVerticalSwipes`) are
 * documented to throw on older clients even when the method exists as a
 * stub — catching is not optional.
 */
function safeCall(fn: () => void): void {
  try {
    fn();
  } catch {
    // Older/unusual clients — degrade silently, never break the app over a chrome call.
  }
}

/** Only calls `fn` if the client's Bot API version is >= `minVersion`. */
function versionGate(minVersion: string, fn: () => void): void {
  if (!tg) return;
  if (!tg.isVersionAtLeast(minVersion)) return;
  safeCall(fn);
}

/**
 * One-time init. Order matches Telegram's own recommended sequence:
 * ready() first, then chrome (expand/colors), then behavior flags.
 */
export function initTelegram(): void {
  if (!tg) return;

  safeCall(() => tg.ready());
  safeCall(() => tg.expand());

  // Matched to --bg so there is no seam between native chrome and content.
  safeCall(() => tg.setHeaderColor("#0a0a0b"));
  safeCall(() => tg.setBackgroundColor("#0a0a0b"));

  safeCall(() => tg.enableClosingConfirmation());

  // Bot API 7.7+ — without this, Telegram's pull-down-to-close gesture
  // fights any vertically-draggable content (and will fight the 3D
  // canvas once Phase 4 lands). Needs verification on a real iOS device
  // per the brief — flagged for Phase 2's manual QA pass.
  versionGate("7.7", () => tg.disableVerticalSwipes());
}

// ---------------------------------------------------------------- haptics

let lastHapticAt = 0;
const HAPTIC_MIN_INTERVAL_MS = 50;

export type HapticImpact = "light" | "medium" | "heavy" | "rigid" | "soft";
export type HapticNotification = "error" | "success" | "warning";

/**
 * Throttled to ~1 per 50ms per the brief — Telegram silently drops spam
 * and unthrottled haptics read as cheap. Version-gated (6.1+) and
 * try/catch'd because it's documented to throw on some clients.
 */
export function hapticImpact(style: HapticImpact = "light"): void {
  if (!tg?.HapticFeedback) return;
  const now = performance.now();
  if (now - lastHapticAt < HAPTIC_MIN_INTERVAL_MS) return;
  lastHapticAt = now;
  versionGate("6.1", () => tg.HapticFeedback!.impactOccurred(style));
}

export function hapticNotification(type: HapticNotification): void {
  if (!tg?.HapticFeedback) return;
  const now = performance.now();
  if (now - lastHapticAt < HAPTIC_MIN_INTERVAL_MS) return;
  lastHapticAt = now;
  versionGate("6.1", () => tg.HapticFeedback!.notificationOccurred(type));
}

export function hapticSelection(): void {
  if (!tg?.HapticFeedback) return;
  const now = performance.now();
  if (now - lastHapticAt < HAPTIC_MIN_INTERVAL_MS) return;
  lastHapticAt = now;
  versionGate("6.1", () => tg.HapticFeedback!.selectionChanged());
}
